import type { ComponentPropsWithoutRef, FunctionComponent } from 'react'
import React, { useEffect, useState } from 'react'

import classnames from 'classnames'

import { TypographyV2 } from '../Seatbelt2/Foundations/Typography'
import styles from './CheckButton.module.scss'

export const CheckButton: FunctionComponent<Props> = ({
  ariaLabel,
  id,
  primaryLabel,
  secondaryLabel,
  name,
  className,
  selected = false,
  disabled = false,
  value,
  onChangeCallback,
  buttonStyle = 'squared',
  ...rest
}) => {
  const [isSelected, setIsSelected] = useState<boolean>(selected)

  useEffect(() => {
    setIsSelected(selected)
  }, [selected])

  const onChange = () => {
    if (!disabled) {
      setIsSelected(!isSelected)

      if (typeof onChangeCallback === 'function') {
        onChangeCallback({ id, value })
      }
    }
  }

  return (
    <div
      data-testid="check-button-wrapper"
      className={classnames(
        styles.checkButtonWrapper,
        {
          [styles.checkButtonWrapperSelected]: isSelected,
          [styles.checkButtonWrapperDisabled]: disabled,
        },
        className
      )}
      {...rest}
    >
      <input
        aria-label={ariaLabel}
        checked={isSelected}
        className={styles.checkButtonInput}
        disabled={disabled}
        id={id}
        name={name}
        onChange={onChange}
        type="checkbox"
        value={value}
      />
      <TypographyV2
        textStyle="sb-text-interface-body-small-regular"
        tag="label"
        aria-hidden={ariaLabel ? true : false}
        className={classnames(styles.labelWrapper, {
          [styles.labelWrapperSquared]: buttonStyle === 'squared',
          [styles.labelWrapperRounded]: buttonStyle === 'rounded',
          [styles.labelWrapperSelected]: isSelected,
          [styles.labelWrapperDisabled]: disabled,
        })}
        data-testid="label-wrapper"
        htmlFor={id}
      >
        <span
          className={classnames(styles.innerLabel, {
            [styles.innerLabelSquared]: buttonStyle === 'squared',
            [styles.innerLabelRounded]: buttonStyle === 'rounded',
            [styles.innerLabelSelected]: buttonStyle === 'squared' && isSelected,
            [styles.innerLabelRoundedSelected]: buttonStyle === 'rounded' && isSelected,
            [styles.innerLabelDisabled]: disabled,
          })}
        >
          <span className={styles.primaryLabel}>{primaryLabel}</span>
          {secondaryLabel && <span className={styles.secondaryLabel}>{`(${secondaryLabel})`}</span>}
        </span>
      </TypographyV2>
    </div>
  )
}

///////// IMPLEMENTATION /////////

type Props = {
  ariaLabel?: string
  id: string
  name: string
  value: string
  primaryLabel: React.ReactNode
  secondaryLabel?: string
  className?: string
  selected?: boolean
  disabled?: boolean
  onChangeCallback: ({ id, value }: { id?: string; value?: string }) => void
  buttonStyle?: 'squared' | 'rounded'
} & ComponentPropsWithoutRef<'div'>
